import { z } from 'zod';
import { isoDate } from './schemas/date';
import { serviceTypeFilterValue, type NO_SERVICE_TYPE } from './schemas/spine';
import type { WithheldField } from './mcp/caps';
import { roundHalfUp } from './money';
import {
  firstOfMonth,
  lastOfMonth,
  twelveMonthsTo,
  yearEarlierRange,
  type ReportFigure,
} from './reports';

/**
 * The Revenue page (ops insights Phase 2): what the shop invoiced, or collected, in a period, how
 * that compares with the same days a year earlier, what kind of work earned it and who did it.
 *
 * Deliberately not a sixteenth entry in `REPORT_KEYS`, on the leaderboard's precedent
 * (`leaderboard.ts`): that catalogue is closed at fifteen, and every member answers in
 * `ReportResult`, whose one table and flat figures cannot hold this page's sections, their
 * leftover rows, a chart and (later) a drill. The page has its own procedure on the report
 * router, `report.revenue`, and this module is its contract: the input, the payload, and the
 * pure pieces both ends share.
 *
 * ⭐ The server sends CENTS and counts, never a percentage: each compared figure comes as the
 * figure and the same figure over the same days a year earlier (`yearEarlierRange`, beside
 * `presetRange` in `reports.ts`). The browser formats them, and works out each row's share of the total with the one share helper, `sharePct`, and each change with the one
 * change helper, `yearEarlierChange`.
 */

/**
 * The change against the same days a year earlier, in percent to one decimal:
 * `(now − a year earlier) ÷ a year earlier`. 180,000 against 140,000 is 28.6.
 *
 * Null when the year-earlier figure is zero or less: there is nothing to be a change OF, and a
 * shop with nothing a year ago must not read "+100%" or "∞". The page then shows "—" and
 * "nothing a year earlier".
 *
 * A half rounds away from zero (`roundHalfUp`), so a rise and a fall of the same size read the
 * same (+6.3 and −6.3), and a change too small to show is 0, never −0. The server sends both
 * figures and never this number: the rule is written here once, for every place that shows it.
 */
export function yearEarlierChange(now: number, yearEarlierFigure: number): number | null {
  if (yearEarlierFigure <= 0) return null;
  // Tenths of a percent, from one division of whole numbers, so the rounding sees the exact
  // quotient. `|| 0`: a fall rounding to nothing is −0, which would print a sign on nothing.
  const tenths = roundHalfUp(((now - yearEarlierFigure) * 1000) / yearEarlierFigure);
  return tenths / 10 || 0;
}

/**
 * What the page's figures are measured in, the Invoiced | Collected switch (ticket 07, spec Q2),
 * which changes every number on the page: the figures, the chart, both sections and the drill.
 *
 *   invoiced   Revenue (CONTEXT.md): invoice subtotals before tax, by the day each invoice was
 *              issued; drafts, voids and late fees out.
 *   collected  Collected (CONTEXT.md): payments received less refunds made, each on the day it
 *              happened, tax and fees in. Each payment follows the invoice it paid to that
 *              invoice's job, whenever the invoice was issued, and so to its service type and its
 *              people; a refund comes off the same rows, in the same proportions, in the period
 *              it was made.
 *
 * Invoiced is first, and so the default: never written into the page's address.
 */
export const REVENUE_BASES = ['invoiced', 'collected'] as const;
export type RevenueBasis = (typeof REVENUE_BASES)[number];

/** The page's period and basis, the fields its drill's input shares. */
const revenuePeriod = { from: isoDate, to: isoDate, basis: z.enum(REVENUE_BASES) };

/** A period whose first day is after its last is refused, on the page and in its drill. */
const inOrder = (v: { from: string; to: string }) => v.from <= v.to;
const OUT_OF_ORDER = { message: 'The period starts after it ends', path: ['to'] };

/**
 * `report.revenue`'s input: calendar days in the SHOP's zone, both ends inclusive, and the
 * basis. The zone is not an input: the server reads it from the shop, so a caller cannot bring
 * a second clock (`runReport`'s rule). `.strict()`, so a stray field is refused, not ignored.
 */
export const revenuePageInput = z
  .object(revenuePeriod)
  .strict()
  .refine(inOrder, OUT_OF_ORDER);
export type RevenuePageInput = z.infer<typeof revenuePageInput>;

/**
 * The leftover row for revenue on no job: an invoice billed with no job, or one whose job is
 * not this shop's. Not a uuid, so it can never be a service type's id.
 */
export const NOT_TIED_TO_A_JOB = 'not-tied';

/** The leftover rows' names, as the page and its CSV print them. */
export const NO_SERVICE_TYPE_LABEL = 'No service type';
export const NOT_TIED_TO_A_JOB_LABEL = 'Not tied to a job';

/**
 * A figure of the period beside the same figure over the same days a year earlier
 * (`yearEarlierRange` of the period). Both amounts, never a percentage: the change is
 * `yearEarlierChange`, worked out where it is shown.
 */
export interface ComparedCents {
  cents: number;
  yearEarlierCents: number;
}

/** A count of the period beside the same count over the same days a year earlier. */
export interface ComparedCount {
  count: number;
  yearEarlierCount: number;
}

/**
 * An average of the period beside the same average a year earlier, each null in a range with
 * nothing to average: an average over nothing is not zero.
 */
export interface ComparedAverage {
  cents: number | null;
  yearEarlierCents: number | null;
}

/**
 * The chart's window (spec "The chart"): the twelve calendar months ending with the month that
 * holds the period's last day, whatever day the period starts on (`twelveMonthsTo`). So "This
 * year" on Sep 28 draws Oct 2025 – Sep 2026, not January to September, and for "Last 12
 * months" the window IS the period, and its twelve months add up to the headline.
 */
export interface RevenueChartWindow {
  /** From the first of the month 11 months before `to`'s, through `to`, in the shop's days. */
  range: { from: string; to: string };
  /** Its twelve months, oldest first, as `YYYY-MM`: the keys every month is filled under. */
  months: string[];
  /**
   * The same window a year earlier (`yearEarlierRange`), so the last month stops on the same
   * day in both years: Sep 1–28, 2026 beside Sep 1–28, 2025, never beside all of September.
   */
  yearEarlier: { from: string; to: string };
  /**
   * `to`, when it is before its month's last day: that month is in progress, and the page
   * draws it hatched ("Sep, to the 28th"). Null when `to` ends its month.
   */
  partialThrough: string | null;
  /**
   * `yearEarlier.to`, when it is before ITS month's last day: the last month's year-earlier
   * figure is then of part of that month, and the page names its days ("Sep 1–28, 2025").
   * Usually the month in progress a year back; also a Feb 28 compared with a leap February,
   * which leaves Feb 29 out (ticket 04's literal rule). Null when it is the whole month.
   */
  yearEarlierThrough: string | null;
}

/** `day` when it is before the last day of its month, else null: a month cut short. */
function cutShort(day: string): string | null {
  const [y, m] = day.split('-').map(Number) as [number, number];
  return day < lastOfMonth(y, m) ? day : null;
}

/**
 * The chart's window for a period ending on `to` (a `YYYY-MM-DD` in the shop's zone). String
 * calendar arithmetic, as `presetRange` does it: no instant, no offset, no DST.
 */
export function revenueChartWindow(to: string): RevenueChartWindow {
  const [y, m] = to.split('-').map(Number) as [number, number];
  const range = twelveMonthsTo(to);
  const yearEarlier = yearEarlierRange(range);
  return {
    range,
    months: Array.from({ length: 12 }, (_, i) => firstOfMonth(y, m - 11 + i).slice(0, 7)),
    yearEarlier,
    partialThrough: cutShort(to),
    yearEarlierThrough: cutShort(yearEarlier.to),
  };
}

/**
 * One month of the chart: what the shop invoiced (or, under Collected, collected) in it, beside
 * the same month a year earlier. Every month of the window is here, 0 when nothing happened: a
 * month missing from the chart would read as a gap in the data, not as a month with nothing in
 * it. Under Collected a month can be below zero, when its refunds outran what arrived.
 */
export interface RevenueTrendMonth extends ComparedCents {
  /** `YYYY-MM`, in the shop's zone. */
  month: string;
  /**
   * The last day counted, on the month in progress (`RevenueChartWindow.partialThrough`); null
   * on every other month. Its year-earlier figure stops on the same day a year earlier.
   */
  partialThrough: string | null;
  /**
   * The last day its year-earlier figure counts, when that is before the end of the month a
   * year back (`RevenueChartWindow.yearEarlierThrough`, the last month only); null when the
   * figure is of the whole month. The page names the days from it and works out none.
   */
  yearEarlierThrough: string | null;
}

/**
 * The four headline figures, on the page's basis. The first three are compared.
 *
 *   On Invoiced                       On Collected
 *   Invoiced                          Collected
 *   Jobs invoiced                     Jobs paid for
 *   Average job                       Average per job paid for
 *   Collected in the same days        Invoiced in the same days
 */
export interface RevenueKpis {
  /**
   * Invoiced: revenue (before tax, late fees out) on invoices issued in the days. Collected:
   * payments received less refunds made in the days, which can be below zero.
   */
  total: ComparedCents;
  /**
   * The colour of `total`, decided by the server, in a report figure's vocabulary: `brand` for
   * Invoiced, as every report paints it; for Collected `success` only above zero
   * (`collectedTone`), and none otherwise, since a period whose refunds outran what arrived has
   * no good news to paint.
   */
  totalTone?: ReportFigure['tone'];
  /**
   * Invoiced: distinct jobs of this shop with a revenue invoice in the days. Collected: distinct
   * jobs of this shop reached by a PAYMENT received in the days; a refund adds no job.
   */
  jobs: ComparedCount;
  /**
   * The amount on those jobs (invoiced, or collected: payments and refunds alike) divided by
   * the jobs, to the cent, in each range.
   */
  averageJob: ComparedAverage;
  /**
   * The OTHER basis over the period's days: Collected on the Invoiced page (money received for
   * any invoice, so it is not a collection rate, and the page says so); Invoiced on the
   * Collected page. Not compared.
   */
  otherBasisCents: number;
}

/**
 * One service type of the shop with revenue in the period or a year earlier: its amount in
 * each. A type with revenue only a year earlier keeps its row: 0 now, no jobs, and no average.
 */
export interface RevenueServiceTypeRow extends ComparedCents {
  kind: 'type';
  /** The service type's id. */
  key: string;
  /** Its name NOW: a type renamed since shows its current name. */
  label: string;
  /** Its place in the shop's list, which is what picks its colour. */
  sortOrder: number;
  /**
   * Jobs invoiced in the period, or under Collected jobs paid for (a refund adds no job, in a
   * row as in the headline: README gap ruling 8); 0 on a row there only for a year earlier, or
   * under Collected on a row holding only refunds.
   */
  jobs: number;
  /** `cents` over `jobs`, to the cent; null with no job in the period. */
  averageJobCents: number | null;
}

/**
 * Revenue on this shop's jobs that have no service type (or another shop's). Its key is the Jobs
 * list's "No service type" filter value, `NO_SERVICE_TYPE`.
 */
export interface RevenueUntypedRow extends ComparedCents {
  kind: 'none';
  key: typeof NO_SERVICE_TYPE;
  label: typeof NO_SERVICE_TYPE_LABEL;
  jobs: number;
  averageJobCents: number | null;
}

/** Revenue on no job of this shop's. It has no jobs, so no average either. */
export interface RevenueNotTiedRow extends ComparedCents {
  kind: 'not-tied';
  key: typeof NOT_TIED_TO_A_JOB;
  label: typeof NOT_TIED_TO_A_JOB_LABEL;
  jobs: null;
  averageJobCents: null;
}

/**
 * Revenue by service type: the shop's types with revenue in the period OR a year earlier,
 * largest first (in the period), then the two leftover rows, always last. They add up to
 * `kpis.total` to the cent, in each range, and their jobs to `kpis.jobs.count`.
 *
 * A row with jobs in the period shows even when its amount nets to zero (an invoice discounted
 * to nothing is still a job invoiced, and hiding its row would leave a job the rows cannot
 * account for). Otherwise a row shows when its amount, now or a year earlier, is not zero.
 *
 * Under Collected the same rows hold what was collected: each payment under its invoice's job's
 * row, each refund under the row of the payment it gave back, so a row can be below zero. A
 * payment on no invoice of this shop's, or on one with no job of this shop's, is "Not tied to
 * a job".
 */
export type RevenueByServiceTypeRow =
  | RevenueServiceTypeRow
  | RevenueUntypedRow
  | RevenueNotTiedRow;

/**
 * How an amount reached a person (CONTEXT.md "Revenue share"): BY HOURS, their share of all the
 * hours ever logged on the job; or, when nobody logged any, SPLIT EVENLY between the people
 * assigned to its visits. One word each, the same in the value, its label and its field
 * (`byHoursCents`, `splitEvenlyCents`).
 */
export type RevenueShareRule = 'by-hours' | 'split-evenly';

/** The rule totals' and the person rows' names for the two rules, as the page prints them. */
export const BY_HOURS_LABEL = 'By hours';
export const SPLIT_EVENLY_LABEL = 'Split evenly';

/** The row for revenue no rule could credit to a person, and its two reasons' names. */
export const NOT_CREDITED_LABEL = 'Not credited';
export const NO_HOURS_OR_ASSIGNEES_LABEL = 'No hours and nobody assigned';

/**
 * Why an invoice's revenue (or, under Collected, a payment or refund) reached nobody: the two
 * reasons of "Not credited".
 */
export type RevenueNotCreditedReason = 'not-tied' | 'no-hours-or-assignees';

/**
 * Each reason of "Not credited" as the page names it: beside the row's amounts, and on a drill's
 * rows. Not in the page's CSV (ticket 08), which holds Not credited's amount alone.
 */
export const NOT_CREDITED_REASON_LABEL: Record<RevenueNotCreditedReason, string> = {
  'not-tied': NOT_TIED_TO_A_JOB_LABEL,
  'no-hours-or-assignees': NO_HOURS_OR_ASSIGNEES_LABEL,
};

/**
 * One kind of work in a person's revenue, for their mix bar: a service type of the shop's, or
 * "No service type". Never "Not tied to a job": money on no job reaches nobody, so it is in
 * "Not credited", not in anyone's mix.
 */
export type RevenuePersonMix =
  | Pick<RevenueServiceTypeRow, 'kind' | 'key' | 'label' | 'sortOrder' | 'cents'>
  | Pick<RevenueUntypedRow, 'kind' | 'key' | 'label' | 'cents'>;

/**
 * One person credited with part of the period's revenue, or of the same days' a year earlier:
 * their revenue share in each, to the cent, by the same rule (in the period, `byHoursCents +
 * splitEvenlyCents`). Someone with a share only a year earlier keeps their row, at 0 now, with
 * no jobs, no average and no mix.
 *
 * Under Collected, each payment and each refund is shared out on its own, a refund as a
 * negative amount off the same people in the same proportions: so a person's amount, any of
 * its parts and their mix can be below zero, and someone with only a refund in the period has
 * a row with no jobs (a refund adds no job) and no average.
 */
export interface RevenuePersonRow extends ComparedCents {
  userId: string;
  /** Their name now, read by id: someone who has since left the shop keeps it. */
  name: string;
  byHoursCents: number;
  splitEvenlyCents: number;
  /**
   * The distinct jobs of the period they share in (under Collected, reached by a payment): a
   * job two people shared counts once for EACH of them, so the rows' jobs add up to more than
   * `kpis.jobs`. The money is split, so it adds up exactly.
   */
  jobs: number;
  /** `cents` over `jobs`, to the cent; null with no job in the period. */
  averageJobCents: number | null;
  /** Their amount per kind of work, largest first, "No service type" last. */
  mix: RevenuePersonMix[];
}

/**
 * The revenue no rule could credit to a person, in the period (`notTiedCents +
 * noHoursOrAssigneesCents`) and a year earlier, and, in the period, why.
 */
export interface RevenueNotCredited extends ComparedCents {
  /** On an invoice with no job, or with a job that is not this shop's. */
  notTiedCents: number;
  /** On a job nobody logged hours on and nobody is assigned to. */
  noHoursOrAssigneesCents: number;
  /**
   * How many of the period's invoices (under Collected, payments and refunds) nobody was
   * credited on. A count, not an amount: invoices discounted to nothing are still invoices, and
   * a payment and its refund net to nothing are still two, and "Not credited" opens them
   * (ticket 06), as a service type row with a job invoiced for nothing does.
   */
  count: number;
}

/**
 * How the period's revenue was credited, rule by rule. With the third rule total, "Not
 * credited" (`RevenueByPerson.notCredited.cents`, not repeated here), they add up to the
 * headline: Invoiced, or Collected.
 */
export interface RevenueRuleTotals {
  byHoursCents: number;
  splitEvenlyCents: number;
}

/**
 * Revenue by person: the people largest first, then "Not credited". They add up to
 * `kpis.total` to the cent, in each range, because each invoice (under Collected, each payment
 * and each refund) is split on its own and whole. "Not credited" shows when either of its
 * amounts is not zero; the rule totals always do.
 */
export interface RevenueByPerson {
  rows: RevenuePersonRow[];
  notCredited: RevenueNotCredited;
  ruleTotals: RevenueRuleTotals;
}

/** `report.revenue`'s answer. */
export interface RevenuePage {
  /** The calendar days read, in the shop's zone, both ends inclusive. */
  range: { from: string; to: string };
  /** What every change compares with: `yearEarlierRange(range)`, read in the same zone. */
  yearEarlier: { from: string; to: string };
  basis: RevenueBasis;
  kpis: RevenueKpis;
  /**
   * The chart: exactly twelve months, oldest first, over `revenueChartWindow(range.to)` and the
   * same window a year earlier. Not withheld from anyone who may read the page.
   */
  trend: RevenueTrendMonth[];
  byServiceType: RevenueByServiceTypeRow[];
  /**
   * Revenue by person, or `null` for a caller without `report:read-people`: then the server
   * never worked it out, in either range, and `withheld` says so. `null` here means WITHHELD,
   * never "nobody".
   */
  byPerson: RevenueByPerson | null;
  /** What this caller may not see, and why: the page draws the notice from this. */
  withheld: WithheldField[];
  /** What the page's numbers can NOT tell you, in the order they are shown. */
  caveats: string[];
}

// ── The drill (ticket 06) ─────────────────────────────────────────────────────

/**
 * The most rows one drill sends: invoices, or under Collected payments and refunds. A figure
 * with more says how many it has (`count`) and that it counts all of them; the rows shown are
 * the newest (README gap ruling 9).
 */
export const REVENUE_DRILL_CAP = 500;

/**
 * A figure on the page that opens the invoices (under Collected, the payments and refunds)
 * behind it (spec "The drill"): the headline, a month of the chart, a service type row (one of
 * the shop's types by id, "No service type" or "Not tied to a job", by the row's `key`), a
 * person, or "Not credited". Year-earlier figures and averages are not figures that drill.
 */
export const revenueFigure = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('total') }).strict(),
  z
    .object({
      kind: z.literal('month'),
      month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
    })
    .strict(),
  z
    .object({
      kind: z.literal('service-type'),
      key: z.union([serviceTypeFilterValue, z.literal(NOT_TIED_TO_A_JOB)]),
    })
    .strict(),
  z.object({ kind: z.literal('person'), userId: z.string().uuid() }).strict(),
  z.object({ kind: z.literal('not-credited') }).strict(),
]);
export type RevenueFigure = z.infer<typeof revenueFigure>;

/** Said when a month is asked for that the period's chart does not draw. */
export const NOT_ON_THE_CHART = 'That month is not on the chart for this period.';

/**
 * `report.revenueDrill`'s input: the page's own period and basis, and the figure. A month
 * must be one of the twelve the page's chart draws (`revenueChartWindow(to)`), since those are
 * the only months with a bar to open; any other is refused (BAD_REQUEST), never answered.
 */
export const revenueDrillInput = z
  .object({ ...revenuePeriod, figure: revenueFigure })
  .strict()
  .refine(inOrder, OUT_OF_ORDER)
  .refine(
    (v) =>
      v.figure.kind !== 'month' ||
      revenueChartWindow(v.to).months.includes(v.figure.month),
    {
      message: NOT_ON_THE_CHART,
      path: ['figure', 'month'],
      // Only over an input that is otherwise sound: a `to` that is not a date has its own
      // refusal already, and has no chart to look for the month on.
      when: (payload) => payload.issues.length === 0,
    },
  );
export type RevenueDrillInput = z.infer<typeof revenueDrillInput>;

/**
 * The days a chart month's figure is of: the month, cut where the chart's window ends (the
 * period's last day, `to`), so the month in progress drills the same days its bar draws.
 * Called only with a month of `revenueChartWindow(to)`, all of which start on their first.
 */
export function chartMonthDays(month: string, to: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return { from: `${month}-01`, to: month === to.slice(0, 7) ? to : lastOfMonth(y, m) };
}

/**
 * How one person's share of one invoice was decided, for a person's drill: BY HOURS, their
 * minutes over every minute logged on the job; or SPLIT EVENLY, one of the people assigned.
 * Numbers, never a percentage: the words are `drillShareWords`'.
 */
export type RevenueDrillShare =
  | { rule: 'by-hours'; minutes: number; jobMinutes: number }
  | { rule: 'split-evenly'; assigned: number };

/**
 * A share in the words a person's drill row says them: "75% of 2 h", "62% of 14.5 h",
 * "1 of 3 assigned".
 *
 * The percent is whole, a half rounded up; a share that is there but rounds to nothing says
 * "under 1%", and one short of all of it that rounds to 100 says "over 99%", so neither reads as
 * a share it is not. The job's time is in hours to one decimal, a whole hour without the ".0"
 * (915 minutes, 15.25 h, is "15.3 h"); under an hour it is in minutes ("45 min"), which one
 * decimal of an hour would blur.
 */
export function drillShareWords(share: RevenueDrillShare): string {
  if (share.rule === 'split-evenly') return `1 of ${share.assigned} assigned`;
  const { minutes, jobMinutes } = share;
  // One division of whole numbers: a true half is exact in binary, so it rounds up.
  const whole = Math.round((minutes * 100) / jobMinutes);
  const pct =
    whole === 0
      ? 'under 1%'
      : whole === 100 && minutes < jobMinutes
        ? 'over 99%'
        : `${whole}%`;
  const of =
    jobMinutes < 60
      ? `${jobMinutes} min`
      : `${(roundHalfUp(jobMinutes / 6) / 10).toLocaleString('en-US', { maximumFractionDigits: 1 })} h`;
  return `${pct} of ${of}`;
}

/** One invoice behind a figure, as its drill lists it. */
export interface RevenueInvoiceRow {
  invoiceId: string;
  invoiceNumber: number;
  /** The day it was issued, in the shop's zone (`YYYY-MM-DD`). */
  issuedOn: string;
  /** The customer's name; null when the invoice names a customer that is not this shop's. */
  customer: string | null;
  /** Its job, when that is a job of this shop's; null is "Not tied to a job". */
  job: { id: string; number: number; title: string } | null;
  /** Commercial parent drilldown, supplied only to owner/office; job remains the execution ID. */
  commercialJob?: { id: string; number: number; title: string } | null;
  /** The job's service type as it is named now; null on a job with none (or no job). */
  serviceType: string | null;
  /** What this figure counts of the invoice: its revenue, or on a person's figure their share. */
  cents: number;
  /** On a person's figure, how their share was decided; null on every other figure. */
  share: RevenueDrillShare | null;
  /** On "Not credited", why nobody was credited; null on every other figure. */
  reason: RevenueNotCreditedReason | null;
}

/**
 * One payment or refund behind a figure under Collected, as its drill lists it: a payment
 * received, or a refund made, with the invoice the payment paid, and what this figure counts
 * of it, with its sign.
 */
export interface RevenueMovementRow {
  /**
   * The payment's id, or the refund's. Unique only within its kind (they are two tables' keys),
   * so a movement is told apart by its kind AND this id: `revenueMovementKey`.
   */
  moveId: string;
  /** A payment received, or a refund made (of a payment; its method, customer and invoice). */
  kind: 'payment' | 'refund';
  /** The day it happened, in the shop's zone (`YYYY-MM-DD`). */
  movedOn: string;
  /** How the payment was made: one of `PAYMENT_METHODS` (for a refund, its payment's). */
  method: string;
  /**
   * The invoice the payment paid, when it is this shop's; null when the payment names none,
   * or one that is not this shop's (which is then "Not tied to a job").
   */
  invoice: { id: string; number: number | null } | null;
  /** The payment's customer's name; null when that is not a customer of this shop's. */
  customer: string | null;
  /** The invoice's job, when that is a job of this shop's; null is "Not tied to a job". */
  job: { id: string; number: number; title: string } | null;
  /** Commercial parent drilldown, supplied only to owner/office; job remains the execution ID. */
  commercialJob?: { id: string; number: number; title: string } | null;
  /** The job's service type as it is named now; null on a job with none (or no job). */
  serviceType: string | null;
  /**
   * What this figure counts of it, signed: a payment's amount, a refund's below zero, or on a
   * person's figure their share of either.
   */
  cents: number;
  /** On a person's figure, how their share was decided; null on every other figure. */
  share: RevenueDrillShare | null;
  /** On "Not credited", why nobody was credited; null on every other figure. */
  reason: RevenueNotCreditedReason | null;
}

/**
 * A payment's or refund's identity, as one string: its kind and its id. The id alone is not
 * enough: a payment's id and a refund's are keys of two tables, and nothing makes them differ.
 * The one key a drill's movements are told apart by, on the server and in the page.
 */
export const revenueMovementKey = (move: Pick<RevenueMovementRow, 'kind' | 'moveId'>) =>
  `${move.kind}:${move.moveId}`;

/** What every drill answer says, whatever its rows are. */
interface DrillCommon {
  /** The days the figure is of: the period, or for a chart month its days (`chartMonthDays`). */
  range: { from: string; to: string };
  /** Every row behind the figure added up, those not sent included: the page's figure. */
  cents: number;
  /** How many rows are behind the figure. More than `rows.length` when capped. */
  count: number;
}

/**
 * `report.revenueDrill`'s answer: what is behind one figure, which adds up to it, on the
 * page's basis (`basis` says which rows these are). At most `REVENUE_DRILL_CAP` rows are sent,
 * with how many there are and what all of them add up to, so a panel that shows fewer can say
 * so ("Showing 500 of 1,204").
 *
 *   invoiced   the invoices, newest first by issue day, then by invoice number (README gap
 *              ruling 9)
 *   collected  the payments and refunds, newest first by when each happened, then by invoice
 *              number, then by kind (a refund before a payment), then by the movement's id
 *              (the same ruling, on a movement's own instant, made total over the movement's
 *              identity, so which 500 are sent never depends on the plan)
 */
export type RevenueDrillAnswer =
  | (DrillCommon & { basis: 'invoiced'; rows: RevenueInvoiceRow[] })
  | (DrillCommon & { basis: 'collected'; rows: RevenueMovementRow[] });
